import {Box, Table, Tbody, Td, Text, Tooltip, Tr} from "@chakra-ui/react";
import HelpTooltip from "../help/HelpTooltip";
import {AreaStatistics, AudioStorageBucket} from "../types";

type Props = {
    statistics?: AreaStatistics
    variant?: "compact" | "detailed"
    showDraftRow?: boolean
}

type AudioStorageRow = {
    key: string
    label: string
    bucket?: AudioStorageBucket
}

const MISSING_STATISTICS_TOOLTIP = "Audio storage statistics have not been calculated yet. Open the space editor or upload/delete sound files to refresh them.";

const formatBytes = (value?: number) => {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
        return "0 B";
    }
    const units = ["B", "KB", "MB", "GB", "TB"];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size = size / 1024;
        unitIndex += 1;
    }
    return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatBucket = (label: string, bucket?: AudioStorageBucket) => {
    const count = bucket?.totalFilesCount || 0;
    const countLabel = `${count} file${count === 1 ? "" : "s"}`;
    return `${label}: ${countLabel} / ${formatBytes(bucket?.totalFilesStorageSize)}`;
};

const getBucketRows = (statistics: AreaStatistics, showDraftRow: boolean): AudioStorageRow[] => {
    const rows: AudioStorageRow[] = [];
    if (showDraftRow) {
        rows.push({
            key: "draft",
            label: "Draft",
            bucket: statistics.audioStorage?.draft
        });
    }
    rows.push({
        key: "published",
        label: "Published",
        bucket: statistics.audioStorage?.published
    });
    rows.push({
        key: "total",
        label: "Total stored",
        bucket: statistics.audioStorage?.total
    });
    return rows;
};

const AreaAudioStorageSummary = ({statistics, variant = "detailed", showDraftRow = true}: Props) => {
    if (!statistics) {
        return (
            <Tooltip label={MISSING_STATISTICS_TOOLTIP} hasArrow>
                <Text as={"span"} color={"gray.500"}>
                    -
                </Text>
            </Tooltip>
        );
    }

    const rows = getBucketRows(statistics, showDraftRow);
    const lines = rows.map((row) => formatBucket(row.label, row.bucket));

    if (variant === "compact") {
        const totalLine = formatBucket("Total stored", statistics.audioStorage?.total);
        const tooltipContent = (
            <Box>
                {lines.map((line) => (
                    <Text key={line} fontSize={"sm"}>
                        {line}
                    </Text>
                ))}
            </Box>
        );
        return (
            <Box display={"inline-flex"} alignItems={"center"}>
                <Text fontSize={"xs"} color={"gray.700"} lineHeight={"short"}>
                    {totalLine}
                </Text>
                <HelpTooltip
                    content={tooltipContent}
                    icon={true}
                    iconAriaLabel={"Audio storage details"}
                />
            </Box>
        );
    }

    return (
        <Table size={"sm"} variant={"simple"} width={"auto"} maxWidth={"520px"}>
            <Tbody>
                {rows.map((row) => {
                    const count = row.bucket?.totalFilesCount || 0;
                    return (
                        <Tr key={row.key}>
                            <Td pl={0} fontWeight={"medium"} color={"gray.700"}>{row.label}</Td>
                            <Td color={"gray.600"}>{count} file{count === 1 ? "" : "s"}</Td>
                            <Td pr={0} textAlign={"right"} color={"gray.600"}>{formatBytes(row.bucket?.totalFilesStorageSize)}</Td>
                        </Tr>
                    );
                })}
            </Tbody>
        </Table>
    );
};

export default AreaAudioStorageSummary;
